import React, { useRef } from "react";
import jsPDF from "jspdf";
import { Download, Printer, MessageCircle, X, CheckCircle2 } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";

const NO_DECIMAL = new Set(["CLP", "HTG"]);
const fmt = (n, c) => {
  const x = Number(n || 0);
  if (NO_DECIMAL.has(c)) return `${Math.round(x).toLocaleString("en-US")} ${c}`;
  return `${x.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;
};

export default function SendCashReceipt({ tx, onClose }) {
  const ref = useRef(null);
  const { brand } = useBranding();
  if (!tx) return null;

  const created = tx.created_at ? new Date(tx.created_at) : new Date();
  const brandName = brand.brand_name || "My GlobalTopup";

  const lines = [
    brandName,
    "Send Cash receipt",
    "",
    `Service:        Send Cash`,
    `Wallet:         ${tx.wallet_provider_label || tx.wallet_provider}`,
    `Full name:      ${tx.recipient_name}`,
    `Phone:          ${tx.recipient_phone}`,
    `Amount paid:    ${fmt(tx.base_amount || tx.amount_sent || tx.amount, tx.currency)}`,
    `Convenience fee:${fmt(tx.convenience_fee || 0, tx.currency)}`,
    `Total paid:     ${fmt(tx.total_paid || tx.amount_sent || tx.amount, tx.currency)}`,
    `Receiver gets:  ${fmt(tx.amount_received, tx.receive_currency || "HTG")}`,
    `Reference:      ${tx.reference}`,
    `Date/Time:      ${created.toLocaleString("en-US")}`,
    "",
    "Your request has been received and is being processed.",
    `Thank you for choosing ${brandName}.`,
  ];

  const text = lines.join("\n");

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: [320, 560] });
    doc.setFont("courier", "bold");
    doc.setFontSize(18);
    doc.text(brandName, 160, 36, { align: "center" });
    doc.setFontSize(11);
    doc.setFont("courier", "normal");
    doc.text("Send Cash receipt", 160, 56, { align: "center" });
    doc.setLineWidth(0.5);
    doc.line(20, 70, 300, 70);
    doc.setFontSize(11);
    let y = 92;
    const rows = [
      ["Service", "Send Cash"],
      ["Wallet", tx.wallet_provider_label || tx.wallet_provider || ""],
      ["Full name", tx.recipient_name || ""],
      ["Phone", tx.recipient_phone || ""],
      ["Amount paid", fmt(tx.base_amount || tx.amount_sent || tx.amount, tx.currency)],
      ["Convenience fee", fmt(tx.convenience_fee || 0, tx.currency)],
      ["Total paid", fmt(tx.total_paid || tx.amount_sent || tx.amount, tx.currency)],
      ["Receiver gets", fmt(tx.amount_received, tx.receive_currency || "HTG")],
      ["Reference", tx.reference || ""],
      ["Date/Time", created.toLocaleString("en-US")],
    ];
    rows.forEach(([k, v]) => {
      doc.setFont("courier", "bold");
      doc.text(`${k}:`, 24, y);
      doc.setFont("courier", "normal");
      doc.text(String(v), 130, y);
      y += 18;
    });
    y += 12;
    doc.line(20, y, 300, y); y += 18;
    doc.setFont("courier", "normal");
    doc.setFontSize(10);
    const note = "Your request has been received\nand is being processed.";
    note.split("\n").forEach((ln) => { doc.text(ln, 160, y, { align: "center" }); y += 14; });
    y += 12;
    doc.setFontSize(9);
    doc.text(`Thank you for choosing ${brandName}.`, 160, y, { align: "center" });
    doc.save(`${brandName}-${tx.reference}.pdf`);
  };

  const printReceipt = () => {
    const w = window.open("", "_blank", "width=400,height=600");
    if (!w) return;
    w.document.write(`
      <html><head><title>${brandName} · ${tx.reference}</title>
        <style>
          body{font-family:'Courier New',monospace;padding:24px;color:#0f172a;}
          h1{font-size:20px;margin:0 0 4px;text-align:center;}
          h2{font-size:12px;margin:0 0 16px;font-weight:normal;text-align:center;color:#64748b;}
          hr{border:none;border-top:1px dashed #94a3b8;margin:12px 0;}
          dl{margin:0;}
          dt{font-weight:bold;display:inline-block;width:140px;}
          dd{margin:0 0 6px 0;display:inline-block;}
          .row{margin:6px 0;}
          .footer{text-align:center;font-size:11px;color:#0f172a;margin-top:16px;line-height:1.6;}
        </style>
      </head><body>
        <h1>${brandName}</h1>
        <h2>Send Cash receipt</h2>
        <hr/>
        <div class="row"><b>Service:</b> Send Cash</div>
        <div class="row"><b>Wallet:</b> ${tx.wallet_provider_label || ""}</div>
        <div class="row"><b>Full name:</b> ${tx.recipient_name || ""}</div>
        <div class="row"><b>Phone:</b> ${tx.recipient_phone || ""}</div>
        <div class="row"><b>Amount paid:</b> ${fmt(tx.base_amount || tx.amount_sent || tx.amount, tx.currency)}</div>
        <div class="row"><b>Convenience fee:</b> ${fmt(tx.convenience_fee || 0, tx.currency)}</div>
        <div class="row"><b>Total paid:</b> ${fmt(tx.total_paid || tx.amount_sent || tx.amount, tx.currency)}</div>
        <div class="row"><b>Receiver gets:</b> ${fmt(tx.amount_received, tx.receive_currency || "HTG")}</div>
        <div class="row"><b>Reference:</b> ${tx.reference || ""}</div>
        <div class="row"><b>Date/Time:</b> ${created.toLocaleString("en-US")}</div>
        <hr/>
        <div class="footer">Your request has been received<br/>and is being processed.<br/><br/>Thank you for choosing ${brandName}.</div>
        <script>window.onload=function(){window.print();}</script>
      </body></html>`);
    w.document.close();
  };

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${msg}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full md:max-w-md overflow-hidden" data-testid="send-cash-receipt">
        <div className="bg-emerald-600 text-white px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            <div>
              <div className="font-semibold text-sm">Request received</div>
              <div className="text-[11px] opacity-90">Receipt #{tx.reference}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/15" data-testid="receipt-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div ref={ref} className="px-5 py-4 font-mono text-sm leading-relaxed">
          <div className="text-center mb-3">
            <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{brandName}</div>
            <div className="text-xs text-slate-500">Send Cash receipt</div>
          </div>
          <div className="border-t border-dashed border-slate-300 my-3" />
          <Row label="Service" value="Send Cash" />
          <Row label="Wallet" value={tx.wallet_provider_label || tx.wallet_provider || ""} />
          <Row label="Full name" value={tx.recipient_name || ""} />
          <Row label="Phone" value={tx.recipient_phone || ""} />
          <Row label="Amount paid" value={fmt(tx.base_amount || tx.amount_sent || tx.amount, tx.currency)} />
          <Row label="Convenience fee" value={fmt(tx.convenience_fee || 0, tx.currency)} />
          <Row label="Total paid" value={fmt(tx.total_paid || tx.amount_sent || tx.amount, tx.currency)} />
          <Row label="Receiver gets" value={fmt(tx.amount_received, tx.receive_currency || "HTG")} />
          <Row label="Reference" value={tx.reference || ""} />
          <Row label="Date/Time" value={created.toLocaleString("en-US")} />
          <div className="border-t border-dashed border-slate-300 my-3" />
          <div className="text-center text-xs text-slate-800 leading-relaxed">
            Your request has been received<br />and is being processed.
          </div>
          <div className="text-center text-[10px] text-slate-500 mt-2">Thank you for choosing {brandName}.</div>
        </div>
        <div className="grid grid-cols-3 border-t border-slate-200 bg-slate-50">
          <button onClick={shareWhatsApp} className="flex items-center justify-center gap-1.5 py-3 hover:bg-emerald-50 text-sm font-semibold text-emerald-700 border-r border-slate-200" data-testid="receipt-whatsapp-btn">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </button>
          <button onClick={printReceipt} className="flex items-center justify-center gap-1.5 py-3 hover:bg-slate-100 text-sm font-semibold text-slate-700 border-r border-slate-200" data-testid="receipt-print-btn">
            <Printer className="w-4 h-4" /> Print
          </button>
          <button onClick={downloadPdf} className="flex items-center justify-center gap-1.5 py-3 hover:bg-blue-50 text-sm font-semibold text-[#0F62FE]" data-testid="receipt-pdf-btn">
            <Download className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-2 mb-1">
      <span className="font-bold text-slate-700 min-w-[110px]">{label}:</span>
      <span className="text-slate-900 break-words">{value}</span>
    </div>
  );
}
